import type { ReactNode } from 'react';
import Button from './Button';
import InlineStatus from './InlineStatus';
import SetupTrustHelperBand from './SetupTrustHelperBand';
import {
  getAuthReturnMeta,
  readStoredAuthReturnContext,
  resolveAuthReturnRouteId,
  type AuthReturnRouteId,
} from '../../lib/authReturnContext';

type ScopedBootstrapRouteId =
  | 'import'
  | 'goals'
  | 'alerts'
  | 'automation'
  | 'settings-notifications'
  | 'wallet-hub'
  | 'transactions'
  | 'dashboard'
  | 'portfolio-analytics'
  | 'risk-intelligence'
  | 'rebalancing'
  | 'rebalancing-targets'
  | 'tax-optimisation'
  | 'tax-export';

type BootstrapContinuityMode = 'mounted-proof' | 'route-skeleton';

interface RouteBootstrapConfig {
  mode: BootstrapContinuityMode;
  heading: string;
  description: string;
  skeletonLabel: string;
  skeleton: ReactNode;
}

const ROUTE_BOOTSTRAP_CONFIG: Record<ScopedBootstrapRouteId, RouteBootstrapConfig> = {
  import: {
    mode: 'mounted-proof',
    heading: 'Import Transactions',
    description: 'Mounted source proof, import counts, and the next ledger review stay attached to this route.',
    skeletonLabel: 'Pinecurl is validating the session locally while import proof reconnects on this route.',
    skeleton: (
      <div className="space-y-4" aria-hidden="true">
        <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-5 space-y-4">
          <div className="space-y-2">
            <div className="skeleton h-4 w-40" />
            <div className="skeleton h-7 w-72" />
          </div>
          <div className="grid gap-3 md:grid-cols-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="skeleton h-16 w-full rounded-xl" />
            ))}
          </div>
          <div className="skeleton h-10 w-56 rounded-lg" />
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          {Array.from({ length: 2 }).map((_, index) => (
            <div key={index} className="rounded-xl border border-gray-800 bg-gray-900/60 p-5 space-y-3">
              <div className="skeleton h-5 w-36" />
              <div className="skeleton h-24 w-full rounded-xl" />
              <div className="skeleton h-10 w-32 rounded-lg" />
            </div>
          ))}
        </div>
      </div>
    ),
  },
  goals: {
    mode: 'mounted-proof',
    heading: 'Portfolio Goals',
    description: 'Active-goal proof and drift follow-through stay visible while this route reconnects.',
    skeletonLabel: 'Pinecurl is validating the session locally while goal proof reconnects on this route.',
    skeleton: (
      <div className="space-y-4" aria-hidden="true">
        <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-5 space-y-4">
          <div className="space-y-2">
            <div className="skeleton h-4 w-36" />
            <div className="skeleton h-6 w-64" />
          </div>
          <div className="grid gap-3 md:grid-cols-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="skeleton h-16 w-full rounded-xl" />
            ))}
          </div>
          <div className="skeleton h-10 w-40 rounded-lg" />
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          {Array.from({ length: 2 }).map((_, index) => (
            <div key={index} className="rounded-xl border border-gray-800 bg-gray-900/60 p-5 space-y-3">
              <div className="skeleton h-5 w-36" />
              <div className="skeleton h-24 w-full rounded-xl" />
            </div>
          ))}
        </div>
      </div>
    ),
  },
  alerts: {
    mode: 'route-skeleton',
    heading: 'Alerts',
    description: 'Alert coverage, readiness, and the latest fired proof stay attached to this route.',
    skeletonLabel: 'Pinecurl is validating the session locally while alert coverage reconnects.',
    skeleton: (
      <div className="space-y-6" aria-hidden="true">
        <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-5 space-y-4">
          <div className="skeleton h-6 w-56" />
          <div className="skeleton h-4 w-full max-w-3xl" />
          <div className="grid gap-3 md:grid-cols-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="skeleton h-16 w-full rounded-xl" />
            ))}
          </div>
          <div className="skeleton h-10 w-40 rounded-lg" />
        </div>
        <div className="rounded-xl border border-gray-800 bg-gray-900/60 p-5 space-y-3">
          <div className="skeleton h-5 w-32" />
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="skeleton h-16 w-full rounded-lg" />
          ))}
        </div>
        <div className="rounded-xl border border-gray-800 bg-gray-900/60 p-5 space-y-3">
          <div className="skeleton h-5 w-36" />
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="skeleton h-20 w-full rounded-lg" />
          ))}
        </div>
      </div>
    ),
  },
  automation: {
    mode: 'mounted-proof',
    heading: 'Automation',
    description: 'Saved advisory rules and the latest recorded proof stay route-owned while this workspace reconnects.',
    skeletonLabel: 'Pinecurl is validating the session locally while automation coverage reconnects.',
    skeleton: (
      <div className="space-y-6" aria-hidden="true">
        <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-5 space-y-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-2 flex-1">
              <div className="skeleton h-6 w-72" />
              <div className="skeleton h-4 w-full max-w-3xl" />
            </div>
            <div className="space-y-2 w-full max-w-sm">
              <div className="skeleton h-4 w-32" />
              <div className="skeleton h-10 w-full rounded-lg" />
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="skeleton h-16 w-full rounded-xl" />
            ))}
          </div>
        </div>
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1.35fr)_minmax(19rem,1fr)]">
          <div className="space-y-6">
            <div className="rounded-xl border border-gray-800 bg-gray-900/60 p-5 space-y-3">
              <div className="skeleton h-5 w-36" />
              <div className="skeleton h-24 w-full rounded-xl" />
              <div className="skeleton h-24 w-full rounded-xl" />
            </div>
            <div className="rounded-xl border border-gray-800 bg-gray-900/60 p-5 space-y-3">
              <div className="skeleton h-5 w-40" />
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="skeleton h-40 w-full rounded-xl" />
              ))}
            </div>
          </div>
          <div className="space-y-6">
            {Array.from({ length: 2 }).map((_, index) => (
              <div key={index} className="rounded-xl border border-gray-800 bg-gray-900/60 p-5 space-y-3">
                <div className="skeleton h-5 w-40" />
                <div className="skeleton h-20 w-full rounded-xl" />
                <div className="skeleton h-10 w-full rounded-lg" />
              </div>
            ))}
          </div>
        </div>
      </div>
    ),
  },
  'settings-notifications': {
    mode: 'route-skeleton',
    heading: 'Notification Preferences',
    description: 'Saved readiness, blocker repair, and carried alert proof stay local to this settings surface.',
    skeletonLabel: 'Pinecurl is validating the session locally while notification readiness reconnects.',
    skeleton: (
      <div className="space-y-6" aria-hidden="true">
        <div className="space-y-2">
          <div className="skeleton h-8 w-56" />
          <div className="skeleton h-4 w-96 max-w-full" />
        </div>
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="rounded-xl border border-gray-800 bg-gray-900/60 p-6 space-y-3">
            <div className="skeleton h-5 w-40" />
            <div className="skeleton h-4 w-full" />
            {index === 0 ? (
              <div className="flex gap-3">
                <div className="skeleton h-6 w-28 rounded-full" />
                <div className="skeleton h-6 w-28 rounded-full" />
              </div>
            ) : null}
            {index === 1 ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {Array.from({ length: 9 }).map((_, gridIndex) => (
                  <div key={gridIndex} className="skeleton h-16 w-full rounded-lg" />
                ))}
              </div>
            ) : null}
            {index === 2 ? (
              <div className="flex items-center justify-between gap-3">
                <div className="space-y-2 flex-1">
                  <div className="skeleton h-4 w-40" />
                  <div className="skeleton h-4 w-56" />
                </div>
                <div className="skeleton h-6 w-24 rounded-full" />
                <div className="skeleton h-6 w-12 rounded-lg" />
              </div>
            ) : null}
          </div>
        ))}
      </div>
    ),
  },
  'wallet-hub': {
    mode: 'mounted-proof',
    heading: 'Wallet Hub',
    description: 'Source coverage and wallet-follow-through stay attached to this route while it reconnects.',
    skeletonLabel: 'Pinecurl is validating the session locally while Wallet Hub coverage reconnects.',
    skeleton: (
      <div className="space-y-6" aria-hidden="true">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="space-y-2">
            <div className="skeleton h-10 w-48" />
            <div className="skeleton h-4 w-80" />
          </div>
          <div className="skeleton h-10 w-44 rounded-lg" />
        </div>
        <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-5 space-y-4">
          <div className="skeleton h-6 w-72" />
          <div className="skeleton h-4 w-full max-w-3xl" />
          <div className="flex flex-wrap gap-3">
            <div className="skeleton h-10 w-40 rounded-lg" />
            <div className="skeleton h-10 w-40 rounded-lg" />
            <div className="skeleton h-10 w-40 rounded-lg" />
          </div>
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          {Array.from({ length: 2 }).map((_, index) => (
            <div key={index} className="rounded-xl border border-gray-800 bg-gray-900/60 p-5 space-y-3">
              <div className="skeleton h-5 w-40" />
              <div className="skeleton h-24 w-full rounded-xl" />
              <div className="skeleton h-24 w-full rounded-xl" />
            </div>
          ))}
        </div>
      </div>
    ),
  },
  transactions: {
    mode: 'mounted-proof',
    heading: 'Transactions',
    description: 'Reconciliation proof, provenance, and ledger review stay attached to this route.',
    skeletonLabel: 'Pinecurl is validating the session locally while this route reconnects.',
    skeleton: (
      <div className="space-y-4" aria-hidden="true">
        <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-5 space-y-4">
          <div className="space-y-2">
            <div className="skeleton h-4 w-36" />
            <div className="skeleton h-6 w-72" />
          </div>
          <div className="grid gap-3 md:grid-cols-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="skeleton h-16 w-full rounded-xl" />
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-gray-800 bg-gray-900/60 p-5 space-y-3">
          <div className="skeleton h-4 w-40" />
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="skeleton h-12 w-full rounded-lg" />
          ))}
        </div>
      </div>
    ),
  },
  dashboard: {
    mode: 'mounted-proof',
    heading: 'Portfolio Dashboard',
    description: 'Portfolio triage stays recognizable while dashboard widgets reconnect.',
    skeletonLabel: 'Pinecurl is validating the session locally while this route reconnects.',
    skeleton: (
      <div className="space-y-6" aria-hidden="true">
        <div className="bg-gradient-to-r from-primary-600/5 to-primary-600/0 border-b border-border py-6 px-1">
          <div className="skeleton h-4 w-40 mb-2" />
          <div className="skeleton h-12 w-64 mb-3" />
          <div className="skeleton h-6 w-32" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="bg-surface border border-border rounded-xl p-4">
              <div className="skeleton h-3 w-20 mb-2" />
              <div className="skeleton h-7 w-16" />
            </div>
          ))}
        </div>
        <div className="bg-surface border border-border rounded-xl p-6">
          <div className="skeleton h-6 w-48 mb-4" />
          <div className="skeleton h-48 w-full rounded-lg" />
        </div>
      </div>
    ),
  },
  'portfolio-analytics': {
    mode: 'mounted-proof',
    heading: 'Portfolio Analytics',
    description: 'Analytics range, charts, and owner proof stay route-owned during reconnect.',
    skeletonLabel: 'Pinecurl is validating the session locally while this route reconnects.',
    skeleton: (
      <div className="space-y-6" aria-hidden="true">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="skeleton h-8 w-56" />
            <div className="skeleton h-4 w-72" />
          </div>
          <div className="flex gap-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="skeleton h-9 w-16 rounded-full" />
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-5">
          <div className="skeleton h-5 w-44 mb-3" />
          <div className="skeleton h-16 w-full rounded-xl" />
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-xl border border-gray-800 bg-gray-900/60 p-5">
            <div className="skeleton h-5 w-32 mb-4" />
            <div className="skeleton h-56 w-full rounded-xl" />
          </div>
          <div className="rounded-xl border border-gray-800 bg-gray-900/60 p-5">
            <div className="skeleton h-5 w-32 mb-4" />
            <div className="skeleton h-56 w-full rounded-xl" />
          </div>
        </div>
      </div>
    ),
  },
  'risk-intelligence': {
    mode: 'mounted-proof',
    heading: 'Risk Intelligence Center',
    description: 'Ranked mitigation proof keeps this risk surface recognizable while it reconnects.',
    skeletonLabel: 'Pinecurl is validating the session locally while this route reconnects.',
    skeleton: (
      <div className="space-y-6" aria-hidden="true">
        <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-5 space-y-4">
          <div className="skeleton h-4 w-40" />
          <div className="grid gap-4 md:grid-cols-2">
            <div className="skeleton h-24 w-full rounded-xl" />
            <div className="skeleton h-24 w-full rounded-xl" />
          </div>
        </div>
        <div className="grid gap-4 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="rounded-xl border border-gray-800 bg-gray-900/60 p-4">
              <div className="skeleton h-4 w-20 mb-3" />
              <div className="skeleton h-10 w-full rounded-lg" />
            </div>
          ))}
        </div>
        <div className="rounded-xl border border-gray-800 bg-gray-900/60 p-5">
          <div className="skeleton h-5 w-40 mb-4" />
          <div className="grid gap-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="skeleton h-24 w-full rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    ),
  },
  rebalancing: {
    mode: 'mounted-proof',
    heading: 'Rebalancing Advisor',
    description: 'Drift proof and the advisory winner stay tied to this route while it reconnects.',
    skeletonLabel: 'Pinecurl is validating the session locally while this route reconnects.',
    skeleton: (
      <div className="space-y-6" aria-hidden="true">
        <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-5 space-y-4">
          <div className="skeleton h-4 w-36" />
          <div className="grid gap-4 md:grid-cols-2">
            <div className="skeleton h-24 w-full rounded-xl" />
            <div className="skeleton h-24 w-full rounded-xl" />
          </div>
        </div>
        <div className="rounded-xl border border-gray-800 bg-gray-900/60 p-5">
          <div className="skeleton h-5 w-40 mb-4" />
          <div className="grid gap-3">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="skeleton h-14 w-full rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    ),
  },
  'rebalancing-targets': {
    mode: 'mounted-proof',
    heading: 'Rebalancing Targets',
    description: 'Saved target proof and the next corrective target action stay tied to this route while it reconnects.',
    skeletonLabel: 'Pinecurl is validating the session locally while saved target proof reconnects on this route.',
    skeleton: (
      <div className="space-y-6" aria-hidden="true">
        <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-5 space-y-4">
          <div className="skeleton h-4 w-40" />
          <div className="grid gap-4 md:grid-cols-2">
            <div className="skeleton h-24 w-full rounded-xl" />
            <div className="skeleton h-24 w-full rounded-xl" />
          </div>
          <div className="skeleton h-10 w-36 rounded-lg" />
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          {Array.from({ length: 2 }).map((_, index) => (
            <div key={index} className="rounded-xl border border-gray-800 bg-gray-900/60 p-5 space-y-3">
              <div className="skeleton h-5 w-36" />
              <div className="skeleton h-24 w-full rounded-xl" />
              <div className="skeleton h-10 w-full rounded-lg" />
            </div>
          ))}
        </div>
      </div>
    ),
  },
  'tax-optimisation': {
    mode: 'mounted-proof',
    heading: 'Tax Optimisation',
    description: 'Tax-decision proof and the selected owner tab stay recognizable during reconnect.',
    skeletonLabel: 'Pinecurl is validating the session locally while this route reconnects.',
    skeleton: (
      <div className="space-y-6" aria-hidden="true">
        <div className="flex items-center gap-2 border-b border-white/10 pb-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="skeleton h-10 w-28 rounded-t-lg" />
          ))}
        </div>
        <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-5 space-y-4">
          <div className="skeleton h-4 w-40" />
          <div className="skeleton h-6 w-72" />
          <div className="flex flex-wrap gap-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="skeleton h-8 w-24 rounded-full" />
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-gray-800 bg-gray-900/60 p-5">
          <div className="skeleton h-5 w-48 mb-4" />
          <div className="grid gap-3">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="skeleton h-12 w-full rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    ),
  },
  'tax-export': {
    mode: 'mounted-proof',
    heading: 'Tax Export',
    description: 'This preserved route keeps the canonical export-owner handoff visible while it reconnects.',
    skeletonLabel: 'Pinecurl is validating the session locally while preserved export proof reconnects.',
    skeleton: (
      <div className="space-y-6" aria-hidden="true">
        <div className="space-y-2">
          <div className="skeleton h-4 w-40" />
          <div className="skeleton h-8 w-44" />
          <div className="skeleton h-4 w-96 max-w-full" />
        </div>
        <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-5 space-y-4">
          <div className="skeleton h-5 w-40" />
          <div className="skeleton h-20 w-full rounded-xl" />
          <div className="grid gap-3 md:grid-cols-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="skeleton h-16 w-full rounded-xl" />
            ))}
          </div>
          <div className="skeleton h-16 w-full rounded-xl" />
          <div className="skeleton h-10 w-60 rounded-lg" />
        </div>
      </div>
    ),
  },
};

export function isChunk332ProtectedBootstrapRoute(
  routeId: AuthReturnRouteId | null,
): routeId is ScopedBootstrapRouteId {
  return (
    routeId === 'import' ||
    routeId === 'goals' ||
    routeId === 'alerts' ||
    routeId === 'automation' ||
    routeId === 'settings-notifications' ||
    routeId === 'wallet-hub' ||
    routeId === 'transactions' ||
    routeId === 'dashboard' ||
    routeId === 'portfolio-analytics' ||
    routeId === 'risk-intelligence' ||
    routeId === 'rebalancing' ||
    routeId === 'rebalancing-targets' ||
    routeId === 'tax-optimisation' ||
    routeId === 'tax-export'
  );
}

export function isChunk332ProtectedBootstrapPath(pathname: string) {
  return isChunk332ProtectedBootstrapRoute(resolveAuthReturnRouteId(pathname));
}

export default function ProtectedRouteBootstrapContinuity({ pathname }: { pathname: string }) {
  const routeId = resolveAuthReturnRouteId(pathname);

  if (!isChunk332ProtectedBootstrapRoute(routeId)) {
    return null;
  }

  const config = ROUTE_BOOTSTRAP_CONFIG[routeId];
  const storedContext = readStoredAuthReturnContext();
  const routeContext =
    storedContext?.routeId === routeId && storedContext.returnTo.startsWith(pathname)
      ? storedContext
      : null;
  const returnMeta = getAuthReturnMeta(routeContext);
  const hasMountedProof =
    config.mode === 'mounted-proof' &&
    Boolean(routeContext && (routeContext.proofLabel || routeContext.ownerCtaLabel));

  return (
    <div
      className="min-h-screen bg-app px-4 py-8 md:px-6 lg:px-8"
      data-testid={`protected-route-bootstrap-${routeId}`}
    >
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight text-white">{config.heading}</h1>
          <p className="text-sm text-gray-400">{config.description}</p>
        </div>

        {hasMountedProof && returnMeta ? (
          <div
            className="space-y-4 rounded-xl border border-blue-500/20 bg-blue-500/5 p-5"
            data-testid={`protected-route-bootstrap-proof-${routeId}`}
          >
            <div className="flex flex-wrap gap-2">
              <span className="rounded-full border border-gray-700 bg-gray-950/50 px-3 py-1.5 text-xs text-gray-300">
                Route:{' '}
                <span className="font-mono tabular-nums text-gray-100">{returnMeta.routeLabel}</span>
              </span>
              {routeContext?.proofLabel ? (
                <span className="rounded-full border border-gray-700 bg-gray-950/50 px-3 py-1.5 text-xs text-gray-300">
                  Mounted proof:{' '}
                  <span className="font-mono tabular-nums text-gray-100">{routeContext.proofLabel}</span>
                </span>
              ) : null}
            </div>

            <InlineStatus
              tone="info"
              title={routeContext?.proofLabel ?? `${returnMeta.routeName} proof stays mounted`}
              message="Pinecurl is validating the session locally while keeping the mounted proof as the last truthful answer on this route."
              meta={(
                <span className="font-mono tabular-nums">
                  Route state: validating-session-locally
                </span>
              )}
              className="rounded-xl"
            />

            <SetupTrustHelperBand
              tone="info"
              eyebrow="Mounted proof stays in control"
              message={`${returnMeta.ownerCtaLabel} remains the dominant owner action while this route reconnects.`}
            />

            <div>
              <Button
                variant="primary"
                size="sm"
                disabled
                disabledReason="Available again after Pinecurl finishes local session validation."
              >
                {returnMeta.ownerCtaLabel}
              </Button>
            </div>
          </div>
        ) : (
          <div
            className="space-y-4"
            data-testid={`protected-route-bootstrap-skeleton-${routeId}`}
          >
            <InlineStatus
              tone="info"
              title={`${config.heading} is reconnecting`}
              message={config.skeletonLabel}
              meta={(
                <span className="font-mono tabular-nums">
                  Route state: validating-session-locally
                </span>
              )}
              className="rounded-xl"
            />
            {config.skeleton}
          </div>
        )}
      </div>
    </div>
  );
}
